import React from 'react'

import Checkbox from '../Form/Checkbox'
import styles from './_treeListBox.module.scss'
import { Button, Cell, Icon, Row } from '../../module'
import {
  type CheckboxListProps,
  CheckboxState,
} from '../Form/NestedCheckboxHelper'

const CheckboxList: React.FC<CheckboxListProps> = ({
  items,
  getStateForId,
  idsToRender = [],
  onClick = () => {},
  expandedItems = {},
  onToggleExpand = () => {},
  indentLevel = 0,
  tableConfig,
  idKey,
  parentKey,
  displayKey,
  pathKey,
  className = '',
}) => {
  const indentSize = 24
  if (!idsToRender.length) {
    const uniqueIds = new Set(
      items.filter((i) => !i[parentKey]).map((i) => i[idKey]),
    )
    idsToRender = Array.from(uniqueIds)
  }

  const getChildNodes = (parentId: string) => {
    const seenIds = new Set()
    const nodeItems = items.filter((i) => {
      return (
        i[parentKey] === parentId &&
        !seenIds.has(i[idKey]) &&
        seenIds.add(i[idKey])
      )
    })

    if (!nodeItems.length) return null
    return (
      <CheckboxList
        items={items}
        idsToRender={nodeItems.map((i) => i[idKey])}
        onClick={onClick}
        expandedItems={expandedItems}
        onToggleExpand={onToggleExpand}
        getStateForId={getStateForId}
        indentLevel={indentLevel + 1}
        tableConfig={tableConfig}
        idKey={idKey}
        parentKey={parentKey}
        displayKey={displayKey}
        pathKey='pathId'
        className={className}
      />
    )
  }

  return (
    <>
      {idsToRender.map((id) => {
        const item = items.find((i) => i[idKey] === id)!
        const checkboxState = getStateForId(id)
        const isExpanded = expandedItems[id]
        const hasChildren = items.some((i) => i[parentKey] === id)

        return (
          <React.Fragment key={`${item[idKey]}-${item[pathKey]}`}>
            <Row className={className}>
              {tableConfig.map((column, index) => (
                <Cell
                  key={index}
                  style={{
                    ...tableConfig[index].style,
                    paddingLeft: index === 0 ? indentLevel * indentSize : 0, // Apply indentation only for the first column
                  }}
                  className={styles.treeCellContainer}
                >
                  {column.dataKey === displayKey ? (
                    <div className={styles.checkboxWrap}>
                      <Checkbox
                        label={''}
                        hideLabel
                        checked={checkboxState === CheckboxState.CHECKED}
                        callout={() => {
                          onClick(item[idKey])
                        }}
                        radioSize='12'
                        partialChecked={checkboxState === CheckboxState.PARTIAL}
                      />
                      {column.children(item, index)}
                    </div>
                  ) : null}
                  {column.dataKey !== displayKey
                    ? column.children(item, index)
                    : null}
                </Cell>
              ))}
              <Cell
                className={styles.treeCellContainer}
                // Override the base cell styles to meet the design requirements.
                style={{
                  padding: '4px 10px',
                }}
              >
                {hasChildren ? (
                  <Button as='unstyled' onClick={() => onToggleExpand(id)}>
                    <Icon
                      icon='caretDown'
                      className={`${styles.caret} ${styles.CaretIcon} ${
                        isExpanded ? styles.flipped : ''
                      }`}
                      color='purple'
                      iconSize='16px'
                    />
                  </Button>
                ) : null}
              </Cell>
            </Row>
            {isExpanded ? getChildNodes(item[idKey]) : null}
          </React.Fragment>
        )
      })}
    </>
  )
}

export default CheckboxList
